import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  dataTableHeadClass,
  DataTablePanel,
  PageShell,
} from '../../components/DataTablePanel';
import { FilterField, ListFiltersBar } from '../../components/ListFiltersBar';
import {
  Callout,
  ListEmptyState,
  LoadingSpinner,
  TextInput,
} from '../../components/ui';
import { ApiError, api } from '../../services/api';
import { t } from '../../i18n';
import { ShipmentContainerBlReport } from '../../types/reports';
import { ReportExportButtons } from './ReportExportButtons';
import { exportShipmentContainerBl } from './exportShipmentReports';

export function ShipmentContainerBlPage() {
  const labels = t().reports;
  const [q, setQ] = useState('');
  const [report, setReport] = useState<ShipmentContainerBlReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setReport(null);
      setLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      void (async () => {
        setLoading(true);
        setError(null);
        try {
          const data = await api.getShipmentContainerBlReport(term);
          if (!cancelled) setReport(data);
        } catch (err) {
          if (!cancelled) {
            setError(err instanceof ApiError ? err.message : t().common.error);
          }
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [q]);

  return (
    <PageShell>
      <ListFiltersBar
        actions={
          <ReportExportButtons
            disabled={!report || report.data.length === 0 || loading}
            onExcel={() => {
              if (!report) return;
              exportShipmentContainerBl({ report, format: 'excel' });
            }}
            onPdf={() => {
              if (!report) return;
              exportShipmentContainerBl({ report, format: 'pdf' });
            }}
          />
        }
      >
        <FilterField label={labels.searchContainerBl} wide>
          <TextInput
            value={q}
            size="sm"
            showClearButton={Boolean(q)}
            placeholder={labels.searchMinChars}
            onChange={(e) => setQ(e.target.value)}
          />
        </FilterField>
      </ListFiltersBar>
      {error ? <Callout variant="danger">{error}</Callout> : null}
      {q.trim().length < 2 ? (
        <Callout variant="info">{labels.searchMinChars}</Callout>
      ) : loading ? (
        <LoadingSpinner message={t().common.loading} />
      ) : !report || report.data.length === 0 ? (
        <ListEmptyState title={t().common.empty} />
      ) : (
        <DataTablePanel className="min-h-0 flex-1">
          <table className="min-w-full text-left text-sm">
            <thead className={dataTableHeadClass}>
              <tr className="border-b border-line text-xs uppercase tracking-wide text-muted">
                <th className="px-4 py-3 font-semibold">{labels.shipmentFileNo}</th>
                <th className="px-4 py-3 font-semibold">{t().common.customer}</th>
                <th className="px-4 py-3 font-semibold">{labels.blNo}</th>
                <th className="px-4 py-3 font-semibold">{labels.containers}</th>
              </tr>
            </thead>
            <tbody>
              {report.data.map((row) => (
                <tr key={row.id} className="border-b border-line/70">
                  <td className="px-4 py-3">
                    <Link
                      to={`/shipment-files/${row.id}`}
                      className="font-medium text-teal-700 hover:underline dark:text-teal-300"
                    >
                      {row.fileNo}
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <Link to={`/customers/${row.customer.id}`} className="hover:underline">
                      {row.customer.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3">{row.blNo ?? '—'}</td>
                  <td className="px-4 py-3">
                    {row.containers.map((c) => c.containerNo).join(', ') || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DataTablePanel>
      )}
    </PageShell>
  );
}
